import { useEffect, useMemo, useState } from "react";
import { useLocation } from "wouter";
import { useQueryClient } from "@tanstack/react-query";
import {
  useMyProjects,
  useNotifications,
  useMyReferrals,
  useProjectDocuments,
  useProjectMessages,
  useSendMessage,
  useApproveQuote,
  useCreateReferral,
  useMarkNotificationRead,
  useMarkAllNotificationsRead,
  useDepositCheckout,
  useProposalHtml,
  useFinanceConfig,
  useFinanceUrlClick,
  useContractReady,
  useContractSync,
} from "@/api/hooks";
import type { Project, Referral } from "@/api/types";
import { useAuth } from "@/_core/hooks/useAuth";
import { LoginPrompt } from "@/components/auth/AuthDialog";
import { HelioHomeLink } from "@/components/HelioLogoMark";
import PortalSwitcher from "@/components/PortalSwitcher";
import { SCORE_NAME, TEAM_NAME, TRADING_NAME } from "@/constants/company";
import { API_BASE } from "@/constants/api";
import {
  CUSTOMER_JOURNEY,
  PORTAL_MESSAGING_ENABLED,
  canApproveQuote,
  canChangeCustomerAvailability,
  customerJourneyIndex,
  customerStageCopy,
  formatPortalDate,
  getNextAction,
  isLiveStage,
  remoteSurveyIsComplete,
  remoteSurveyLockedByStage,
  surveyMilestone,
  type NextActionKind,
} from "@/constants/projectStages";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Input } from "@/components/ui/input";
import { Tabs, TabsContent } from "@/components/ui/tabs";
import { toast } from "sonner";
import { openSignedDocument } from "@/utils/documents";
import {
  buildFinanceApplyUrl,
  DEFAULT_FINANCE_TERM_MONTHS,
} from "@/utils/financePayment";
import ContractSigningModal from "@/components/quote/ContractSigningModal";
import CustomerAvailabilityModal from "@/components/availability/CustomerAvailabilityModal";
import { ProposalHtmlPreview } from "@/components/quote/ProposalHtmlPreview";
import {
  Sun, CheckCircle, Clock, FileText, MessageSquare, Gift,
  Copy, ArrowRight, Loader2, Send, Bell, LogOut, Home,
  Download, CreditCard, FileSignature, Zap, Battery, Car, MapPin, ExternalLink, Camera, Calendar,
} from "lucide-react";
import { motion } from "framer-motion";
import { formatRelativeTime } from "@/utils/date";

const DOCUMENT_CATEGORY_LABELS: Record<string, string> = {
  proposal: "Proposal",
  contract: "Contract",
  compliance: "Contract",
  survey: "Survey",
  photo: "Survey photos",
  visualisation: "Visualisation",
  vision: "Visualisation",
  utility_bill: "Energy bill",
  bill: "Energy bill",
  other: "Other",
};

const DOCUMENT_CATEGORY_ORDER = [
  "proposal", "contract", "compliance", "survey", "photo",
  "visualisation", "vision", "utility_bill", "bill", "other",
];

function gbp(value?: number | null): string {
  if (value == null || Number.isNaN(Number(value))) return "—";
  return new Intl.NumberFormat("en-GB", {
    style: "currency",
    currency: "GBP",
    maximumFractionDigits: 0,
  }).format(Number(value));
}

function ProjectTimeline({ project }: { project: Project }) {
  const current = Math.max(customerJourneyIndex(project), 0);
  const n = CUSTOMER_JOURNEY.length;
  const lineInset = `${50 / n}%`;
  const trackWidth = `${100 - 100 / n}%`;
  const progressWidth = `${(current / (n - 1)) * (100 - 100 / n)}%`;
  return (
    <div className="relative">
      <div
        className="absolute top-4 h-0.5 bg-white/8"
        style={{ left: lineInset, width: trackWidth }}
      />
      <div
        className="absolute top-4 h-0.5 bg-[#29ffa9] transition-all duration-700"
        style={{ left: lineInset, width: progressWidth }}
      />
      <div className="relative flex justify-between">
        {CUSTOMER_JOURNEY.map(({ key, label }, i) => {
          const done = i < current;
          const active = i === current;
          return (
            <div key={key} className="flex flex-col items-center gap-2" style={{ width: `${100 / n}%` }}>
              <div className={`relative z-10 flex h-8 w-8 items-center justify-center rounded-full border-2 transition-all ${
                done ? "border-[#29ffa9] bg-[#29ffa9]" :
                active ? "border-[#29ffa9] bg-[#29ffa9]/20" :
                "border-white/20 bg-[#0B1F3B]"
              }`}>
                {done ? (
                  <CheckCircle className="h-4 w-4 text-[#050d1a]" />
                ) : active ? (
                  <div className="h-2.5 w-2.5 rounded-full bg-[#29ffa9] animate-pulse" />
                ) : (
                  <div className="h-2 w-2 rounded-full bg-white/20" />
                )}
              </div>
              <span className={`text-[10px] sm:text-xs text-center leading-tight ${
                active ? "text-[#29ffa9] font-semibold" : done ? "text-white/60" : "text-white/25"
              }`}>
                {label}
              </span>
            </div>
          );
        })}
      </div>
    </div>
  );
}

function StageBadge({ stage }: { stage: string }) {
  const copy = customerStageCopy(stage);
  return (
    <span className={`inline-flex items-center rounded-full px-2.5 py-0.5 text-xs font-semibold ${copy.badgeClass}`}>
      {copy.label}
    </span>
  );
}

function ProjectPicker({
  projects,
  selectedId,
  onSelect,
}: {
  projects: Project[];
  selectedId: number;
  onSelect: (id: number) => void;
}) {
  if (projects.length < 2) return null;
  return (
    <div className="flex gap-3 mb-6 flex-wrap">
      {projects.map((p) => (
        <button
          key={p.id}
          onClick={() => onSelect(p.id)}
          className={`rounded-lg border px-4 py-2 text-sm font-medium transition-all ${
            selectedId === p.id
              ? "border-[#29ffa9] bg-[#29ffa9]/10 text-[#29ffa9]"
              : "border-white/10 text-white/60 hover:border-white/20"
          }`}
        >
          {p.reference}
        </button>
      ))}
    </div>
  );
}

export default function CustomerPortal() {
  const [, navigate] = useLocation();
  const queryClient = useQueryClient();
  const { user, loading, isAuthenticated, logout } = useAuth();
  const [activeTab, setActiveTab] = useState("dashboard");
  const [selectedProjectId, setSelectedProjectId] = useState<number | null>(null);
  const [messageText, setMessageText] = useState("");
  const [referralCopied, setReferralCopied] = useState(false);
  const [notifPanelOpen, setNotifPanelOpen] = useState(false);
  const [proposalPreviewOpen, setProposalPreviewOpen] = useState(false);
  const [contractModalOpen, setContractModalOpen] = useState(false);
  const [contractSigningUrl, setContractSigningUrl] = useState<string | null>(null);
  const [availabilityOpen, setAvailabilityOpen] = useState(false);

  const { data: projects, isLoading: projectsLoading } = useMyProjects(isAuthenticated, "customer");
  const { data: notifications } = useNotifications(isAuthenticated);
  const { data: referrals } = useMyReferrals(isAuthenticated);
  const { data: financeConfig } = useFinanceConfig();

  useEffect(() => {
    const params = new URLSearchParams(window.location.search);
    const payment = params.get("payment");
    if (payment === "success") {
      toast.success("Deposit received. Thank you.");
      void queryClient.invalidateQueries({ queryKey: ["projects"] });
    } else if (payment === "cancelled") {
      toast.message("Deposit checkout was cancelled. You can try again when you are ready.");
    }
    if (payment) {
      params.delete("payment");
      params.delete("ref");
      const next = params.toString();
      window.history.replaceState({}, "", window.location.pathname + (next ? `?${next}` : "") + window.location.hash);
    }
  }, [queryClient]);

  const selectedProject = projects?.find((p) => p.id === selectedProjectId) ?? projects?.[0];
  const selectedId = selectedProject?.id ?? 0;

  const { data: documents } = useProjectDocuments(selectedId, !!selectedProject);
  const { data: messages, refetch: refetchMessages } = useProjectMessages(
    selectedId,
    PORTAL_MESSAGING_ENABLED && !!selectedProject,
  );
  const { data: proposalHtml, isLoading: proposalHtmlLoading, isError: proposalHtmlError } = useProposalHtml(
    proposalPreviewOpen ? selectedProject?.id : undefined,
  );

  const sendMessageMutation = useSendMessage(selectedId);
  const approveQuote = useApproveQuote();
  const createReferral = useCreateReferral();
  const markRead = useMarkNotificationRead();
  const markAllRead = useMarkAllNotificationsRead();
  const createDeposit = useDepositCheckout();
  const financeUrlClick = useFinanceUrlClick();
  const contractReady = useContractReady();
  const contractSync = useContractSync();

  const nextAction = selectedProject ? getNextAction(selectedProject) : null;
  const showReferralsNav = projects?.some((p) => isLiveStage(p.stage)) ?? false;

  const groupedDocuments = useMemo(() => {
    if (!documents?.length) return [];
    const groups = new Map<string, typeof documents>();
    for (const doc of documents) {
      const key = doc.category || "other";
      const label = DOCUMENT_CATEGORY_LABELS[key] ?? "Other";
      const existing = groups.get(label) ?? [];
      existing.push(doc);
      groups.set(label, existing);
    }
    return [...groups.entries()].sort((a, b) => {
      const ai = DOCUMENT_CATEGORY_ORDER.findIndex((k) => DOCUMENT_CATEGORY_LABELS[k] === a[0]);
      const bi = DOCUMENT_CATEGORY_ORDER.findIndex((k) => DOCUMENT_CATEGORY_LABELS[k] === b[0]);
      return (ai === -1 ? 99 : ai) - (bi === -1 ? 99 : bi);
    });
  }, [documents]);

  const copyReferral = (code: string) => {
    navigator.clipboard.writeText(`${window.location.origin}/quote?ref=${code}`);
    setReferralCopied(true);
    setTimeout(() => setReferralCopied(false), 2000);
    toast.success("Referral link copied!");
  };

  const downloadProposalPdf = (project: Project) => {
    const a = document.createElement("a");
    a.href = project.proposalPdfUrl ?? `${API_BASE}/api/quotes/${project.id}/proposal-pdf`;
    a.download = `${TRADING_NAME}-Proposal-${project.proposalReference ?? project.reference}.pdf`;
    a.click();
  };

  const handleContinueFinance = async (project: Project) => {
    const loanAmount = project.totalPrice ?? project.lead?.totalPrice ?? 0;
    const termMonths =
      project.financeTermMonths
      || project.lead?.financeTermMonths
      || DEFAULT_FINANCE_TERM_MONTHS;
    const fallbackApplyUrl = buildFinanceApplyUrl(
      financeConfig?.applyUrl
        ?? project.financeUrl
        ?? project.lead?.financeUrl
        ?? "",
      loanAmount,
      termMonths,
    );
    try {
      const clickResult = await financeUrlClick.mutateAsync(project.id);
      const applyUrl = clickResult.applyUrl
        ? buildFinanceApplyUrl(clickResult.applyUrl, loanAmount, termMonths)
        : fallbackApplyUrl;
      window.open(applyUrl, "_blank", "noopener,noreferrer");
    } catch {
      window.open(fallbackApplyUrl, "_blank", "noopener,noreferrer");
    }
  };

  const handleSignContract = async (project: Project) => {
    try {
      const result = await contractReady.mutateAsync(project.id);
      if (result.signing_url) {
        setContractSigningUrl(result.signing_url);
        setContractModalOpen(true);
      } else {
        toast.error(result.message ?? "Could not start contract signing.");
      }
    } catch (error) {
      toast.error(error instanceof Error ? error.message : "Could not start contract signing.");
    }
  };

  const handleContractCompleted = async (project: Project) => {
    try {
      await contractSync.mutateAsync(project.id);
      await queryClient.invalidateQueries({ queryKey: ["projects"] });
      toast.success("Agreement signed — you can now pay your deposit.");
    } catch {
      await queryClient.invalidateQueries({ queryKey: ["projects"] });
      toast.success("Agreement signed. Your portal will update shortly.");
    } finally {
      setContractModalOpen(false);
      setContractSigningUrl(null);
    }
  };

  const handlePayDeposit = (project: Project) => {
    createDeposit.mutate(
      { projectId: project.projectId ?? project.id, origin: window.location.origin },
      {
        onSuccess: (data) => {
          void queryClient.invalidateQueries({ queryKey: ["projects"] });
          if (data?.url) {
            const opened = window.open(data.url, "_blank", "noopener,noreferrer");
            if (!opened) window.location.href = data.url;
          }
        },
        onError: () => toast.error("Could not start deposit checkout"),
      },
    );
  };

  const handleNextAction = (kind: NextActionKind, project: Project) => {
    switch (kind) {
      case "view_proposal":
        setProposalPreviewOpen(true);
        break;
      case "approve_quote":
        approveQuote.mutate(project.id, { onSuccess: () => toast.success("Quote approved!") });
        break;
      case "select_availability":
        setAvailabilityOpen(true);
        break;
      case "sign_contract":
        void handleSignContract(project);
        break;
      case "pay_deposit":
        handlePayDeposit(project);
        break;
      case "continue_finance":
        void handleContinueFinance(project);
        break;
      case "message_team":
        setActiveTab("messages");
        break;
      case "start_remote_survey":
      case "continue_remote_survey":
      case "update_remote_survey":
        navigate(`/portal/survey/${project.id}`);
        break;
    }
  };

  const ctaPending =
    approveQuote.isPending
    || createDeposit.isPending
    || financeUrlClick.isPending
    || contractReady.isPending;

  if (loading) {
    return (
      <div className="min-h-screen bg-[#0B1F3B] flex items-center justify-center">
        <Loader2 className="h-8 w-8 text-[#29ffa9] animate-spin" />
      </div>
    );
  }

  if (!isAuthenticated) {
    return (
      <div className="min-h-screen bg-[#0B1F3B] text-white">
        <LoginPrompt message="Sign in to track your solar installation and view documents." />
      </div>
    );
  }

  const unreadCount = notifications?.filter((n) => !n.readAt).length ?? 0;
  const navItems = [
    { icon: Home, label: "Dashboard", tab: "dashboard" },
    { icon: FileText, label: "Documents", tab: "documents" },
    ...(PORTAL_MESSAGING_ENABLED ? [{ icon: MessageSquare, label: "Messages", tab: "messages" }] : []),
    ...(showReferralsNav ? [{ icon: Gift, label: "Refer a friend", tab: "referrals" }] : []),
  ];

  const messagePartner = selectedProject?.installerCompany || selectedProject?.installerName
    ? (selectedProject.installerCompany ?? selectedProject.installerName)
    : TEAM_NAME;

  return (
    <div className="min-h-screen bg-[#0B1F3B] text-white">
      <div className="flex h-screen overflow-hidden">
        <aside className="w-64 shrink-0 border-r border-white/8 bg-[#0a1628] flex flex-col">
          <div className="flex items-center gap-2 px-6 py-5 border-b border-white/8">
            <HelioHomeLink className="flex items-center gap-2">
              <img src="/powlo-logo-lime.svg" alt="Powlo" className="h-7 w-auto" />
            </HelioHomeLink>
            <Badge className="ml-auto text-xs bg-[#29ffa9]/10 text-[#29ffa9] border-[#29ffa9]/30">Customer</Badge>
          </div>

          <nav className="flex-1 px-3 py-4 space-y-1">
            {navItems.map(({ icon: Icon, label, tab }) => (
              <button
                key={tab}
                className={`w-full flex items-center gap-3 rounded-lg px-3 py-2.5 text-sm transition-colors ${
                  activeTab === tab ? "bg-[#29ffa9]/10 text-[#29ffa9]" : "text-white/60 hover:bg-white/5 hover:text-white"
                }`}
                onClick={() => setActiveTab(tab)}
              >
                <Icon className="h-4 w-4" />
                {label}
              </button>
            ))}
          </nav>

          <div className="border-t border-white/8 p-4">
            <div className="flex items-center gap-3 mb-3">
              <div className="h-8 w-8 rounded-full bg-[#29ffa9]/20 flex items-center justify-center text-[#29ffa9] text-xs font-bold">
                {user?.name?.[0] ?? "U"}
              </div>
              <div className="flex-1 min-w-0">
                <p className="text-sm font-medium truncate">{user?.name ?? "Customer"}</p>
                <p className="text-xs text-white/40 truncate">{user?.email}</p>
              </div>
            </div>
            <PortalSwitcher />
            <button
              onClick={logout}
              className="w-full flex items-center gap-2 rounded-lg px-3 py-2 text-xs text-white/40 hover:text-white/70 hover:bg-white/5 transition-colors"
            >
              <LogOut className="h-3.5 w-3.5" />
              Sign out
            </button>
          </div>
        </aside>

        <main className="flex-1 overflow-y-auto">
          <div className="sticky top-0 z-30 border-b border-white/8 bg-[#0B1F3B]/80 backdrop-blur-xl px-8 py-4 flex items-center justify-between">
            <div>
              <h1 className="font-display text-xl font-semibold">
                Good {new Date().getHours() < 12 ? "morning" : new Date().getHours() < 18 ? "afternoon" : "evening"}, {user?.name?.split(" ")[0] ?? "there"}
              </h1>
              <p className="text-sm text-white/40">
                {projects?.length ? `${projects.length} active project${projects.length > 1 ? "s" : ""}` : "No projects yet"}
              </p>
            </div>
            <button
              onClick={() => setNotifPanelOpen(true)}
              className="relative flex h-9 w-9 items-center justify-center rounded-lg border border-white/10 hover:bg-white/5 transition-colors"
            >
              <Bell className="h-4 w-4 text-white/60" />
              {unreadCount > 0 && (
                <span className="absolute -top-1 -right-1 flex h-4 w-4 items-center justify-center rounded-full bg-[#29ffa9] text-[10px] font-bold text-[#050d1a]">
                  {unreadCount > 9 ? "9+" : unreadCount}
                </span>
              )}
            </button>
          </div>

          <div className="p-8">
            {projectsLoading ? (
              <div className="flex items-center justify-center py-24">
                <Loader2 className="h-8 w-8 text-[#29ffa9] animate-spin" />
              </div>
            ) : !projects?.length ? (
              <div className="text-center py-24">
                <div className="mx-auto mb-6 flex h-16 w-16 items-center justify-center rounded-2xl bg-[#29ffa9]/10 border border-[#29ffa9]/30">
                  <Sun className="h-8 w-8 text-[#29ffa9]" />
                </div>
                <h2 className="font-display text-2xl font-bold mb-3">No projects yet</h2>
                <p className="text-white/50 mb-6">Get a free solar quote to start your journey.</p>
                <Button className="btn-cta" onClick={() => navigate("/quote")}>
                  Get a quote <ArrowRight className="ml-2 h-4 w-4" />
                </Button>
              </div>
            ) : (
              <Tabs value={activeTab} onValueChange={setActiveTab}>
                <TabsContent value="dashboard">
                  <ProjectPicker
                    projects={projects}
                    selectedId={selectedId}
                    onSelect={setSelectedProjectId}
                  />

                  {selectedProject && nextAction && (
                    <div className="space-y-6">
                      <div className="rounded-2xl border border-white/8 bg-[#0d1b2e] p-6">
                        <div className="flex flex-col gap-4 lg:flex-row lg:items-start lg:justify-between mb-6">
                          <div>
                            <div className="flex items-center gap-3 mb-1 flex-wrap">
                              <h2 className="font-display text-xl font-semibold">{selectedProject.reference}</h2>
                              <StageBadge stage={selectedProject.stage} />
                            </div>
                            <p className="text-sm font-medium text-white/80 mt-2">{nextAction.title}</p>
                            <p className="text-sm text-white/50 mt-1 max-w-xl">{nextAction.body}</p>
                            {selectedProject.address && (
                              <p className="text-xs text-white/35 mt-2 flex items-center gap-1.5">
                                <MapPin className="h-3.5 w-3.5" />
                                {[selectedProject.address, selectedProject.postcode].filter(Boolean).join(", ")}
                              </p>
                            )}
                          </div>
                          <div className="flex flex-col items-stretch sm:items-end gap-2 shrink-0">
                            {nextAction.cta && (
                              <Button
                                className="btn-cta"
                                disabled={ctaPending}
                                onClick={() => handleNextAction(nextAction.cta!.kind, selectedProject)}
                              >
                                {ctaPending ? <Loader2 className="h-4 w-4 animate-spin" /> : ctaIcon(nextAction.cta.kind)}
                                {nextAction.cta.label}
                              </Button>
                            )}
                            {nextAction.secondaryCta && (
                              <Button
                                variant="outline"
                                className="border-white/20 text-white/70 hover:text-white"
                                onClick={() => handleNextAction(nextAction.secondaryCta!.kind, selectedProject)}
                              >
                                {ctaIcon(nextAction.secondaryCta.kind)}
                                {nextAction.secondaryCta.label}
                              </Button>
                            )}
                            {canApproveQuote(selectedProject) && nextAction.cta?.kind !== "approve_quote" && (
                              <Button
                                variant="outline"
                                className="border-white/20 text-white/70 hover:text-white"
                                disabled={approveQuote.isPending}
                                onClick={() => approveQuote.mutate(selectedProject.id, { onSuccess: () => toast.success("Quote approved!") })}
                              >
                                {approveQuote.isPending ? <Loader2 className="h-4 w-4 animate-spin mr-2" /> : null}
                                Approve quote
                              </Button>
                            )}
                            {nextAction.cta?.kind === "continue_finance" && financeConfig?.disclosureText && (
                              <p className="text-[11px] text-white/35 max-w-xs text-right leading-relaxed">
                                {financeConfig.disclosureText}
                              </p>
                            )}
                            {selectedProject.depositPaid && (
                              <span className="flex items-center gap-1.5 text-xs font-semibold text-[#29ffa9] bg-[#29ffa9]/10 rounded-full px-3 py-1">
                                <CheckCircle className="h-3.5 w-3.5" />
                                Deposit paid
                                {selectedProject.depositAmount ? ` · ${gbp(selectedProject.depositAmount)}` : ""}
                              </span>
                            )}
                          </div>
                        </div>
                        <ProjectTimeline project={selectedProject} />
                      </div>

                      <MilestoneDates
                        project={selectedProject}
                        onChangeAvailability={
                          canChangeCustomerAvailability(selectedProject)
                            ? () => setAvailabilityOpen(true)
                            : undefined
                        }
                      />

                      <SystemSummary
                        project={selectedProject}
                        previewOpen={proposalPreviewOpen}
                        onTogglePreview={() => setProposalPreviewOpen((open) => !open)}
                        onDownloadPdf={() => downloadProposalPdf(selectedProject)}
                        proposalHtml={proposalHtml}
                        proposalLoading={proposalHtmlLoading}
                        proposalError={proposalHtmlError}
                      />

                      {isLiveStage(selectedProject.stage) && (
                        <button
                          type="button"
                          onClick={() => setActiveTab("referrals")}
                          className="w-full rounded-xl border border-[#29ffa9]/30 bg-[#29ffa9]/5 px-5 py-4 text-left hover:bg-[#29ffa9]/10 transition-colors"
                        >
                          <p className="text-sm font-semibold text-[#29ffa9]">Refer a friend, earn £150</p>
                          <p className="text-xs text-white/50 mt-1">Share your link once your system is live. You both receive £150 after their installation.</p>
                        </button>
                      )}
                    </div>
                  )}
                </TabsContent>

                <TabsContent value="documents">
                  <ProjectPicker
                    projects={projects}
                    selectedId={selectedId}
                    onSelect={setSelectedProjectId}
                  />
                  <div className="rounded-xl border border-white/8 bg-[#0d1b2e] overflow-hidden">
                    <div className="px-6 py-4 border-b border-white/8 flex items-center justify-between">
                      <h2 className="font-semibold">Document library</h2>
                      <Badge className="bg-white/10 text-white/60">{documents?.length ?? 0} files</Badge>
                    </div>
                    {!documents?.length ? (
                      <div className="text-center py-16">
                        <FileText className="h-10 w-10 text-white/20 mx-auto mb-3" />
                        <p className="text-white/40 text-sm">No documents yet. They'll appear here as your project progresses.</p>
                      </div>
                    ) : (
                      groupedDocuments.map(([category, docs]) => (
                        <div key={category}>
                          <div className="px-6 py-2 bg-white/3 text-xs font-semibold uppercase tracking-wider text-white/40">
                            {category}
                          </div>
                          <div className="divide-y divide-white/8">
                            {docs.map((doc) => (
                              <div key={doc.id} className="flex items-center gap-4 px-6 py-4 hover:bg-white/5 transition-colors">
                                <div className="flex h-10 w-10 items-center justify-center rounded-lg bg-[#29ffa9]/10">
                                  <FileText className="h-5 w-5 text-[#29ffa9]" />
                                </div>
                                <div className="flex-1 min-w-0">
                                  <p className="font-medium text-sm truncate">{doc.name}</p>
                                  <p className="text-xs text-white/40">
                                    {doc.createdAt ? new Date(doc.createdAt).toLocaleDateString("en-GB") : "—"}
                                  </p>
                                </div>
                                <Button
                                  size="sm"
                                  variant="outline"
                                  className="border-white/20 text-white/60 hover:text-white"
                                  onClick={() => openSignedDocument(doc.id).catch(() => toast.error("Could not open document"))}
                                >
                                  <Download className="h-3.5 w-3.5" />
                                </Button>
                              </div>
                            ))}
                          </div>
                        </div>
                      ))
                    )}
                  </div>
                </TabsContent>

                {PORTAL_MESSAGING_ENABLED && (
                <TabsContent value="messages">
                  <ProjectPicker
                    projects={projects}
                    selectedId={selectedId}
                    onSelect={setSelectedProjectId}
                  />
                  <div className="rounded-xl border border-white/8 bg-[#0d1b2e] flex flex-col h-[600px]">
                    <div className="px-6 py-4 border-b border-white/8">
                      <h2 className="font-semibold">Messages with {messagePartner}</h2>
                      <p className="text-xs text-white/40 mt-0.5">{selectedProject?.reference}</p>
                    </div>
                    <div className="flex-1 overflow-y-auto p-6 space-y-4">
                      {!messages?.length ? (
                        <div className="text-center py-16">
                          <MessageSquare className="h-10 w-10 text-white/20 mx-auto mb-3" />
                          <p className="text-white/40 text-sm">No messages yet. Send a message to {messagePartner} below.</p>
                        </div>
                      ) : (
                        messages.map((msg) => {
                          const isMe = msg.senderRole === "customer";
                          return (
                            <div key={msg.id} className={`flex ${isMe ? "justify-end" : "justify-start"}`}>
                              <div className={`max-w-[75%] rounded-2xl px-4 py-3 ${isMe ? "bg-[#29ffa9] text-[#050d1a]" : "bg-[#132035] text-white"}`}>
                                <p className="text-sm">{msg.content}</p>
                                <p className={`text-xs mt-1 ${isMe ? "text-[#050d1a]/60" : "text-white/40"}`}>
                                  {formatRelativeTime(msg.createdAt)}
                                </p>
                              </div>
                            </div>
                          );
                        })
                      )}
                    </div>
                    <div className="border-t border-white/8 p-4 flex gap-3">
                      <Input
                        value={messageText}
                        onChange={(e) => setMessageText(e.target.value)}
                        placeholder="Type a message..."
                        className="bg-[#132035] border-white/10 text-white"
                        onKeyDown={(e) => {
                          if (e.key === "Enter" && !e.shiftKey && messageText.trim() && selectedProject) {
                            sendMessageMutation.mutate(messageText.trim(), {
                              onSuccess: () => { setMessageText(""); refetchMessages(); },
                              onError: () => toast.error("Failed to send message"),
                            });
                          }
                        }}
                      />
                      <Button
                        className="btn-cta shrink-0"
                        disabled={!messageText.trim() || sendMessageMutation.isPending}
                        onClick={() => {
                          if (messageText.trim() && selectedProject) {
                            sendMessageMutation.mutate(messageText.trim(), {
                              onSuccess: () => { setMessageText(""); refetchMessages(); },
                              onError: () => toast.error("Failed to send message"),
                            });
                          }
                        }}
                      >
                        <Send className="h-4 w-4" />
                      </Button>
                    </div>
                  </div>
                </TabsContent>
                )}

                <TabsContent value="referrals">
                  <ReferralsPanel
                    referrals={referrals}
                    referralCopied={referralCopied}
                    createPending={createReferral.isPending}
                    onCopy={copyReferral}
                    onGenerate={() => createReferral.mutate()}
                  />
                </TabsContent>
              </Tabs>
            )}
          </div>
        </main>
      </div>

      {notifPanelOpen && (
        <div className="fixed inset-0 z-50 flex">
          <div
            className="flex-1 bg-black/50 backdrop-blur-sm"
            onClick={() => setNotifPanelOpen(false)}
          />
          <motion.div
            initial={{ x: "100%" }}
            animate={{ x: 0 }}
            exit={{ x: "100%" }}
            transition={{ duration: 0.25, ease: [0.23, 1, 0.32, 1] }}
            className="w-full max-w-sm bg-[#0a1628] border-l border-white/8 flex flex-col h-full"
          >
            <div className="flex items-center justify-between px-6 py-5 border-b border-white/8">
              <div className="flex items-center gap-2">
                <Bell className="h-5 w-5 text-[#29ffa9]" />
                <h2 className="font-display font-semibold">Notifications</h2>
                {unreadCount > 0 && (
                  <span className="flex h-5 w-5 items-center justify-center rounded-full bg-[#29ffa9] text-[10px] font-bold text-[#050d1a]">
                    {unreadCount}
                  </span>
                )}
              </div>
              <div className="flex items-center gap-2">
                {unreadCount > 0 && (
                  <button
                    onClick={() => markAllRead.mutate()}
                    className="text-xs text-[#29ffa9] hover:text-[#29ffa9]/80 transition-colors"
                  >
                    Mark all read
                  </button>
                )}
                <button
                  onClick={() => setNotifPanelOpen(false)}
                  className="flex h-8 w-8 items-center justify-center rounded-lg hover:bg-white/5 transition-colors text-white/40 hover:text-white"
                >
                  ✕
                </button>
              </div>
            </div>
            <div className="flex-1 overflow-y-auto">
              {!notifications?.length ? (
                <div className="flex flex-col items-center justify-center py-24 px-6 text-center">
                  <Bell className="h-10 w-10 text-white/15 mb-3" />
                  <p className="text-white/40 text-sm">No notifications yet.</p>
                  <p className="text-white/25 text-xs mt-1">Updates about your project will appear here.</p>
                </div>
              ) : (
                <div className="divide-y divide-white/5">
                  {notifications.map((n) => (
                    <div
                      key={n.id}
                      className={`flex items-start gap-3 px-6 py-4 cursor-pointer transition-colors hover:bg-white/3 ${
                        !n.readAt ? "bg-[#29ffa9]/3" : ""
                      }`}
                      onClick={() => {
                        if (!n.readAt) markRead.mutate(n.id);
                      }}
                    >
                      <div className={`mt-1.5 h-2 w-2 rounded-full shrink-0 ${
                        n.readAt ? "bg-white/15" : "bg-[#29ffa9]"
                      }`} />
                      <div className="flex-1 min-w-0">
                        <p className={`text-sm font-medium leading-snug ${
                          n.readAt ? "text-white/50" : "text-white"
                        }`}>{n.title}</p>
                        {n.body && (
                          <p className="text-xs text-white/35 mt-0.5 leading-relaxed">{n.body}</p>
                        )}
                        <p className="text-xs text-white/20 mt-1.5">
                          {formatRelativeTime(n.createdAt)}
                        </p>
                      </div>
                      {!n.readAt && (
                        <div className="shrink-0 mt-1">
                          <span className="text-[10px] font-semibold text-[#29ffa9] uppercase tracking-wider">New</span>
                        </div>
                      )}
                    </div>
                  ))}
                </div>
              )}
            </div>
            <div className="px-6 py-4 border-t border-white/8">
              <p className="text-xs text-white/25 text-center">Notifications are refreshed automatically</p>
            </div>
          </motion.div>
        </div>
      )}

      <ContractSigningModal
        signingUrl={contractSigningUrl}
        open={contractModalOpen}
        onClose={() => {
          setContractModalOpen(false);
          setContractSigningUrl(null);
        }}
        onCompleted={() => {
          if (selectedProject) {
            void handleContractCompleted(selectedProject);
          }
        }}
      />
      <CustomerAvailabilityModal
        quoteId={selectedProject?.id ?? null}
        open={availabilityOpen}
        onOpenChange={setAvailabilityOpen}
      />
    </div>
  );
}

function ctaIcon(kind: NextActionKind) {
  const className = "h-4 w-4 mr-2";
  switch (kind) {
    case "view_proposal":
      return <FileText className={className} />;
    case "select_availability":
      return <Calendar className={className} />;
    case "sign_contract":
      return <FileSignature className={className} />;
    case "pay_deposit":
      return <CreditCard className={className} />;
    case "continue_finance":
      return <ExternalLink className={className} />;
    case "message_team":
      return <MessageSquare className={className} />;
    case "start_remote_survey":
    case "continue_remote_survey":
    case "update_remote_survey":
      return <Camera className={className} />;
    default:
      return null;
  }
}

function MilestoneDates({
  project,
  onChangeAvailability,
}: {
  project: Project;
  onChangeAvailability?: () => void;
}) {
  const [, navigate] = useLocation();
  const cards: { label: string; value: string; hint?: string; icon: typeof Clock; href?: string; onClick?: () => void }[] = [];
  const survey = surveyMilestone(project);
  const installLabel = formatPortalDate(project.installDate);
  const availableLabel = formatPortalDate(project.customerAvailableDate);
  const journey = customerJourneyIndex(project);
  const canOpenSurvey =
    project.remoteSurveyEnabled !== false
    && Boolean(project.remoteSurveyStatus || project.remoteSurveySubmittedAt)
    && !remoteSurveyLockedByStage(project.stage);

  if (survey) {
    cards.push({
      label: survey.label,
      value: survey.value,
      icon: remoteSurveyIsComplete(project) ? CheckCircle : Clock,
      href: canOpenSurvey ? `/portal/survey/${project.id}` : undefined,
    });
  }
  if (availableLabel && !installLabel) {
    cards.push({
      label: "You're available",
      value: availableLabel,
      hint: onChangeAvailability ? "Change date" : undefined,
      icon: Calendar,
      onClick: onChangeAvailability,
    });
  }
  if (installLabel || journey === 3) {
    cards.push({
      label: "Install date",
      value: installLabel ?? "To be confirmed",
      icon: Sun,
    });
  }

  if (!cards.length) return null;

  return (
    <div className={`grid gap-4 ${cards.length > 1 ? "md:grid-cols-2" : ""}`}>
      {cards.map(({ label, value, hint, icon: Icon, href, onClick }) => {
        const inner = (
          <>
            <div className="flex items-center gap-2 mb-2">
              <Icon className="h-4 w-4 text-[#29ffa9]" />
              <span className="text-xs text-white/40">{label}</span>
            </div>
            <p className="font-semibold text-sm">{value}</p>
            {hint ? <p className="text-xs text-[#29ffa9] mt-2">{hint}</p> : null}
          </>
        );
        if (href || onClick) {
          return (
            <button
              key={label}
              type="button"
              className="rounded-xl border border-white/8 bg-[#0d1b2e] p-4 text-left transition-colors hover:border-[#29ffa9]/40"
              onClick={() => {
                if (href) navigate(href);
                else onClick?.();
              }}
            >
              {inner}
            </button>
          );
        }
        return (
          <div key={label} className="rounded-xl border border-white/8 bg-[#0d1b2e] p-4">
            {inner}
          </div>
        );
      })}
    </div>
  );
}

function SystemSummary({
  project,
  previewOpen,
  onTogglePreview,
  onDownloadPdf,
  proposalHtml,
  proposalLoading,
  proposalError,
}: {
  project: Project;
  previewOpen: boolean;
  onTogglePreview: () => void;
  onDownloadPdf: () => void;
  proposalHtml?: string;
  proposalLoading: boolean;
  proposalError: boolean;
}) {
  const specs = [
    project.systemSizeKw ? { icon: Zap, label: `${project.systemSizeKw} kWp solar` } : null,
    project.batteryKwh && Number(project.batteryKwh) > 0
      ? { icon: Battery, label: `${project.batteryKwh} kWh battery` }
      : { icon: Battery, label: "No battery" },
    { icon: Car, label: project.includesEv ? "EV charger included" : "No EV charger" },
  ].filter(Boolean) as { icon: typeof Zap; label: string }[];

  const expires = formatPortalDate(project.proposalExpiresAt);
  const quoted = project.stage === "quoted" || project.stage === "lead";

  return (
    <div className="rounded-xl border border-white/8 bg-[#0d1b2e] p-6">
      <div className="flex flex-col sm:flex-row sm:items-start sm:justify-between gap-3 mb-4">
        <div>
          <h3 className="font-semibold">Your system</h3>
          {project.selectedTier && (
            <p className="text-xs text-white/40 mt-0.5 capitalize">{project.selectedTier} package</p>
          )}
        </div>
        <div className="flex flex-wrap gap-2">
          {project.proposalHtmlUrl && (
            <Button
              size="sm"
              variant="outline"
              className="border-white/20 text-white/70 hover:text-white"
              onClick={onTogglePreview}
            >
              <FileText className="h-3.5 w-3.5 mr-1.5" />
              {previewOpen ? "Hide proposal" : "View proposal"}
            </Button>
          )}
          {project.proposalPdfUrl && (
            <Button
              size="sm"
              variant="outline"
              className="border-white/20 text-white/70 hover:text-white"
              onClick={onDownloadPdf}
            >
              <Download className="h-3.5 w-3.5 mr-1.5" />
              PDF
            </Button>
          )}
          {project.proposalHtmlUrl && (
            <a
              href={project.proposalHtmlUrl}
              target="_blank"
              rel="noopener noreferrer"
              className="inline-flex items-center rounded-md border border-white/20 px-3 py-1.5 text-xs font-medium text-white/70 hover:text-white"
            >
              <ExternalLink className="h-3.5 w-3.5 mr-1.5" />
              Full page
            </a>
          )}
        </div>
      </div>

      <div className="grid sm:grid-cols-2 gap-4">
        <div className="space-y-2">
          {specs.map(({ icon: Icon, label }) => (
            <div key={label} className="flex items-center gap-2 text-sm text-white/70">
              <Icon className="h-4 w-4 text-white/30 shrink-0" />
              {label}
            </div>
          ))}
          {project.installerCompany || project.installerName ? (
            <p className="text-xs text-white/40 pt-1">Installer: {project.installerCompany ?? project.installerName}</p>
          ) : null}
        </div>
        <div className="space-y-1 text-sm">
          <div className="flex justify-between"><span className="text-white/40">System price</span><span>{gbp(project.totalPrice)}</span></div>
          {project.annualSavings ? (
            <div className="flex justify-between"><span className="text-white/40">Est. annual saving</span><span>{gbp(project.annualSavings)}</span></div>
          ) : null}
          {project.paybackYears ? (
            <div className="flex justify-between"><span className="text-white/40">Payback</span><span>{Number(project.paybackYears).toFixed(1)} years</span></div>
          ) : null}
          {project.annualProduction ? (
            <div className="flex justify-between"><span className="text-white/40">Est. generation</span><span>{Number(project.annualProduction).toLocaleString("en-GB")} kWh/yr</span></div>
          ) : null}
          {project.helioScore != null ? (
            <div className="flex justify-between"><span className="text-white/40">{SCORE_NAME}</span><span>{project.helioScore}</span></div>
          ) : null}
          {quoted && expires ? (
            <div className="flex justify-between"><span className="text-white/40">Proposal valid until</span><span>{expires}</span></div>
          ) : null}
        </div>
      </div>

      {previewOpen && (
        <div className="mt-5">
          {proposalLoading && (
            <div className="flex items-center justify-center py-12 text-white/50 text-sm">
              <Loader2 className="h-5 w-5 animate-spin mr-2" />
              Loading your proposal…
            </div>
          )}
          {proposalError && (
            <p className="text-sm text-white/50 text-center py-8">
              Your proposal is being prepared. Try again in a moment or download the PDF.
            </p>
          )}
          {proposalHtml && !proposalLoading && (
            <ProposalHtmlPreview html={proposalHtml} />
          )}
        </div>
      )}
    </div>
  );
}

function ReferralsPanel({
  referrals,
  referralCopied,
  createPending,
  onCopy,
  onGenerate,
}: {
  referrals?: Referral[];
  referralCopied: boolean;
  createPending: boolean;
  onCopy: (code: string) => void;
  onGenerate: () => void;
}) {
  return (
    <div className="max-w-2xl space-y-6">
      <div className="rounded-2xl border border-[#29ffa9]/30 bg-[#29ffa9]/5 p-8 text-center">
        <div className="mx-auto mb-4 flex h-16 w-16 items-center justify-center rounded-2xl bg-[#29ffa9]/20">
          <Gift className="h-8 w-8 text-[#29ffa9]" />
        </div>
        <h2 className="font-display text-2xl font-bold mb-2">Refer a friend, earn £150</h2>
        <p className="text-white/60 mb-6">Share your unique referral link. When your friend installs solar with {TRADING_NAME}, you both receive £150.</p>

        {referrals && referrals.length > 0 ? (
          <div className="flex items-center gap-3 rounded-xl border border-white/10 bg-[#0d1b2e] p-4">
            <code className="flex-1 text-sm text-[#29ffa9] font-mono text-left truncate">
              {window.location.origin}/quote?ref={referrals[0].referralCode}
            </code>
            <Button size="sm" className="btn-cta shrink-0" onClick={() => onCopy(referrals[0].referralCode)}>
              {referralCopied ? <CheckCircle className="h-4 w-4" /> : <Copy className="h-4 w-4" />}
            </Button>
          </div>
        ) : (
          <Button className="btn-cta" onClick={onGenerate} disabled={createPending}>
            {createPending ? <Loader2 className="h-4 w-4 animate-spin mr-2" /> : null}
            Generate my referral link
          </Button>
        )}
      </div>

      {referrals && referrals.length > 0 && (
        <div className="rounded-xl border border-white/8 bg-[#0d1b2e] overflow-hidden">
          <div className="px-6 py-4 border-b border-white/8">
            <h3 className="font-semibold">Your referrals</h3>
          </div>
          {referrals.map((r) => (
            <div key={r.id} className="flex items-center justify-between px-6 py-4 border-b border-white/8 last:border-0">
              <div>
                <p className="text-sm font-medium">{r.referredEmail ?? "Pending"}</p>
                <p className="text-xs text-white/40">{r.createdAt ? new Date(r.createdAt).toLocaleDateString("en-GB") : "—"}</p>
              </div>
              <div className="flex items-center gap-3">
                <span className={`text-xs font-semibold px-2 py-0.5 rounded-full ${
                  r.status === "paid" ? "bg-[#29ffa9]/20 text-[#29ffa9]" :
                  r.status === "converted" ? "bg-[#3d8eff]/20 text-[#3d8eff]" :
                  "bg-white/10 text-white/50"
                }`}>
                  {r.status}
                </span>
                <span className="font-semibold text-sm">£{Number(r.rewardAmount).toFixed(0)}</span>
              </div>
            </div>
          ))}
        </div>
      )}

      <div className="rounded-xl border border-white/8 bg-[#0d1b2e] p-6">
        <h3 className="font-semibold mb-4">How it works</h3>
        <div className="space-y-3">
          {[
            "Share your unique referral link with friends and family",
            "They get a free solar quote through your link",
            "When they complete their installation, you both earn £150",
            "Reward paid within 30 days of installation completion",
          ].map((step, i) => (
            <div key={i} className="flex items-start gap-3">
              <div className="flex h-6 w-6 shrink-0 items-center justify-center rounded-full bg-[#29ffa9]/10 text-[#29ffa9] text-xs font-bold mt-0.5">
                {i + 1}
              </div>
              <p className="text-sm text-white/60">{step}</p>
            </div>
          ))}
        </div>
      </div>
    </div>
  );
}
